import { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface VideoPlayerProps {
  imageUrl: string;
  duration?: number;
  className?: string;
}

export default function VideoPlayer({ imageUrl, duration = 5, className = "" }: VideoPlayerProps) {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(true);
  const [showControls, setShowControls] = useState(false);
  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const togglePlay = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);
  }, [isPlaying]);

  const handleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  const animationDuration = `${duration * 2}s`;

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden rounded-xl bg-black cursor-pointer group ${className}`}
      style={{ aspectRatio: "16/9" }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => isPlaying && setShowControls(false)}
      onClick={togglePlay}
    >
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          willChange: "transform",
          animation: isPlaying
            ? `ken-burns ${animationDuration} ease-in-out infinite alternate`
            : "none",
        }}
      >
        <img
          src={imageUrl}
          alt={t("generate.player.alt")}
          className="w-full h-full object-cover"
          style={{ minWidth: "120%", minHeight: "120%" }}
          draggable={false}
        />
      </div>

      {/* Play/Pause overlay when paused */}
      {!isPlaying && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity">
          <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <i className="ri-play-fill text-2xl text-white"></i>
          </div>
        </div>
      )}

      {/* Controls bar */}
      <div
        className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-4 py-3 transition-opacity duration-300 ${
          showControls ? "opacity-100" : "opacity-0"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={togglePlay}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
            >
              <i className={isPlaying ? "ri-pause-fill text-lg" : "ri-play-fill text-lg"}></i>
            </button>
            <span className="text-xs text-white/80">{t("generate.player.loop", { count: duration })}</span>
          </div>
          <button
            type="button"
            onClick={handleFullscreen}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
          >
            <i className="ri-fullscreen-line"></i>
          </button>
        </div>
      </div>
    </div>
  );
}